import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import styled from 'styled-components'
import OfferCalendarDayCell from './OfferCalendarDayCell'
import { rem } from 'polished'
import { calcGridPositionsForVerticalMonth, getCalendarMonthEmptyCells, getCalendarWeekStart, getCalendarWeekdayIndex, getLocalDate, orderCalendarWeekdays } from 'lib/datetime/calendarUtils'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Caption from 'LuxKit/Typography/Caption'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import Heading from 'LuxKit/Typography/Heading'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import type { BundledOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import {
  OfferPageActions,
  takeCurrentOfferState,
} from 'contexts/OfferPage/offerPageStateReducer'
import { getCalendarCheapestDays, markCheapestPrice } from 'selectors/offerPage/offerPageSelectors'
import useBundleAndSaveCalendarDayPickingUtils from 'hooks/Offers/BundleAndSave/useBundleAndSaveCalendarDayPickingUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isVillaOffer } from 'lib/offer/offerTypes'
import { getBestVillasDiscounts } from '../../../Villas/utils/calculateDiscount'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import { getComplementaryBundleState } from 'selectors/offerPage/bundledOfferPageSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { EmptyObject } from 'lib/object/objectUtils'
import { getFixedFlashCalendarDayState, getFixedFlashCalendarSelectionMode } from '../fixedFlashLengthOfStayCalendarUtils'

const BaseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const Grid = styled(BaseGrid)`
  position: relative;
  margin-top: ${rem(8)};
`

const EmptyCell = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${rem(8)} ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  box-shadow: 0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground},
    1px 0 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset,
  0 -1px 0 0 ${props => props.theme.palette.neutral.surface.lightForeground} inset;
`

interface Props {
  days: Array<App.CalendarDay>
  tacticalDays: Array<string>
  selectedCheckIn?: string
  selectedCheckOut?: string
  checkoutCalendarDays: Record<string, App.CalendarDay>
  checkInCalendarDays: Record<string, App.CalendarDay>
  maximumDuration?: number
  minimumStayTooltipDate?: string
  maximumStayTooltipDate?: string
  hoveredCheckOut?: string
  onMinimumStayDisabledClick: (day: App.CalendarDay) => void
  onMaximumStayDisabledClick: (day: App.CalendarDay) => void
  onClearStayTooltips: () => void
  onHoveredCheckOutChange: (checkOut?: string) => void
  className?: string
  currentOfferId?: string
  bundleOfferId?: string
  duration: number
  cacheActive?: boolean
  otherAvailability: { [checkIn: string]: boolean }
  isRoomSelected?: boolean
  flightsOnly?: boolean
  enableCalendarRecommendations: boolean
  isAnyFlightsActive: boolean
  complementaryBundleState?: BundledOfferState
  cheapestDays: Record<string, App.CalendarCheapestDay>
  offer: App.Offer
  displayMemberPrice: boolean
  userPoints: number
  rooms: Array<App.Occupants>
  markCheapestPrice: boolean
  isFixedFlashLengthOfStayEnabled?: boolean
  minimumDuration?: number
  canLoyaltyUpgrade?: boolean
  tier: App.LoyaltyTier
}

function checkHasPromo(day: App.CalendarDay) {
  return !!day.dealId && day.flightPrice > 0
}

function OfferCalendarDaysMonth(props: Props) {
  const {
    className,
    days,
    tacticalDays,
    duration,
    rooms,
    enableCalendarRecommendations,
    otherAvailability,
    selectedCheckIn,
    selectedCheckOut,
    checkoutCalendarDays,
    checkInCalendarDays,
    maximumDuration,
    minimumStayTooltipDate,
    maximumStayTooltipDate,
    hoveredCheckOut,
    onMinimumStayDisabledClick,
    onMaximumStayDisabledClick,
    onClearStayTooltips,
    onHoveredCheckOutChange,
    isRoomSelected,
    flightsOnly,
    isAnyFlightsActive,
    currentOfferId,
    bundleOfferId,
    complementaryBundleState,
    cheapestDays,
    offer,
    displayMemberPrice,
    userPoints,
    markCheapestPrice,
    isFixedFlashLengthOfStayEnabled = false,
    minimumDuration,
    canLoyaltyUpgrade,
    tier,
  } = props

  const loyaltyProductType = getLoyaltyProductType(offer)

  const intl = useIntl()
  const weekStart = getCalendarWeekStart(intl.locale)

  const dayNames = [
    {
      short: intl.formatMessage({
        id: 'Ky9GfSIi5Rr_0Bj6fiQy',
        defaultMessage: 'Mon',
        description: 'Short abbreviated day name for Monday shown in the calendar day header row',
      }),
      full: intl.formatMessage({
        id: 'lLcq8wwAeo8yR9UEHloA',
        defaultMessage: 'Monday',
        description: 'Full day name for Monday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: '3wUKXX9ArW3H3EBoCGJA',
        defaultMessage: 'Tue',
        description: 'Abbreviated day name for Tuesday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'A6V-YCo6yC0Sseh50UMT',
        defaultMessage: 'Tuesday',
        description: 'Full day name for Tuesday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'o8pdOSQJkZoNg5HKIUjb',
        defaultMessage: 'Wed',
        description: 'Abbreviated day name for Wednesday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'Lz42L4UpKCXJkImJFX_E',
        defaultMessage: 'Wednesday',
        description: 'Full day name for Wednesday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'j8tASNhaMksnI_tSqknd',
        defaultMessage: 'Thu',
        description: 'Abbreviated day name for Thursday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'DO1sBh6hZAYU66oN_be7',
        defaultMessage: 'Thursday',
        description: 'Full day name for Thursday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: '52owzA--L--HfrruyZqh',
        defaultMessage: 'Fri',
        description: 'Short abbreviated day name for Friday shown in the calendar day header row',
      }),
      full: intl.formatMessage({
        id: '3nwMHsNEVMfuCQ7zljyJ',
        defaultMessage: 'Friday',
        description: 'Full day name for Friday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: 'HaMvQSO8j-DcpLAK53gN',
        defaultMessage: 'Sat',
        description: 'Abbreviated day name for Saturday in calendar header',
      }),
      full: intl.formatMessage({
        id: 'ashmCW-uRWl5QjCDPT9l',
        defaultMessage: 'Saturday',
        description: 'Full day name for Saturday, used as tooltip in calendar header',
      }),
    },
    {
      short: intl.formatMessage({
        id: '2KG-7GbkjYSds4YO6rmv',
        defaultMessage: 'Sun',
        description: 'Short abbreviated day name for Sunday shown in the calendar day header row',
      }),
      full: intl.formatMessage({
        id: 'elZh_u2EPND55nIKEGYg',
        defaultMessage: 'Sunday',
        description: 'Full day name for Sunday, used as tooltip in calendar header',
      }),
    },
  ]

  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const firstDate = days[0]

  const startCellStyle = useMemo(() => ({
    gridColumnStart: getCalendarWeekdayIndex(getLocalDate(firstDate.checkIn), weekStart) + 1,
  }), [firstDate, weekStart])

  const emptyCells = useMemo(() => getCalendarMonthEmptyCells(firstDate, weekStart), [firstDate, weekStart])

  const betweenRange = useMemo(() => {
    const previewCheckOut = isFixedFlashLengthOfStayEnabled && selectedCheckIn && !selectedCheckOut ? hoveredCheckOut : selectedCheckOut
    if (selectedCheckIn && previewCheckOut) {
      return calcGridPositionsForVerticalMonth(
        getLocalDate(firstDate.checkIn),
        getLocalDate(selectedCheckIn),
        getLocalDate(previewCheckOut),
      ).range
    }
    return undefined
  }, [selectedCheckIn, selectedCheckOut, hoveredCheckOut, firstDate, isFixedFlashLengthOfStayEnabled])

  const {
    isComplementaryBundleCell,
    isComplementaryCheckInCell,
    isComplementaryCheckOutCell,
    isIllegalBundleSelection,
  } = useBundleAndSaveCalendarDayPickingUtils(complementaryBundleState, duration)
  const fixedFlashSelectionMode = getFixedFlashCalendarSelectionMode({
    isFixedFlashLengthOfStayEnabled,
    selectedCheckIn,
    selectedCheckOut,
    bundleOfferId,
  })
  const isFixedFlashLengthOfStayDateSelection = fixedFlashSelectionMode === 'select-check-out'
  const fixedFlashCheckoutCalendarDays = isFixedFlashLengthOfStayEnabled ? checkoutCalendarDays : EmptyObject
  const fixedFlashCheckInCalendarDays = isFixedFlashLengthOfStayEnabled ? checkInCalendarDays : EmptyObject
  const fixedFlashMaximumDuration = isFixedFlashLengthOfStayEnabled ? maximumDuration : undefined
  const minimumCheckoutDuration = useMemo(() => {
    if (!selectedCheckIn) return

    const durations = Object.keys(fixedFlashCheckoutCalendarDays)
      .map(checkOut => moment(checkOut).diff(selectedCheckIn, 'days'))
      .filter(candidateDuration => candidateDuration > 0)

    return durations.length ? Math.min(...durations) : undefined
  }, [fixedFlashCheckoutCalendarDays, selectedCheckIn])
  const minimumStayTooltipDescription = minimumCheckoutDuration === undefined ? undefined : intl.formatMessage(
    {
      id: '3O7r9dxCwhgC1g9Phllp',
      defaultMessage: '{nights, plural, one {# night minimum} other {# nights minimum}}',
      description: 'Tooltip shown when a checkout date is before the minimum stay requirement in the offer page calendar',
    },
    { nights: minimumCheckoutDuration },
  )
  const maximumStayTooltipDescription = fixedFlashMaximumDuration === undefined ? undefined : intl.formatMessage(
    {
      id: 'PNKY6m2zJVzKiEVV559F',
      defaultMessage: '{nights, plural, one {# night maximum} other {# nights maximum}}',
      description: 'Tooltip shown when a check-in date is before the maximum stay requirement in the offer page calendar',
    },
    { nights: fixedFlashMaximumDuration },
  )

  const onDayClick = useCallback((day: App.CalendarDay) => {
    onClearStayTooltips()
    const action = fixedFlashSelectionMode === 'select-check-out' && selectedCheckIn && moment(day.checkIn).isAfter(selectedCheckIn, 'day') ?
      CALENDAR_GA_EVENT_ACTION.CheckOut :
      CALENDAR_GA_EVENT_ACTION.CheckIn
    onUserAnalyticsAction?.(action, moment(day.checkIn).format(ISO_DATE_FORMAT))
  }, [onUserAnalyticsAction, selectedCheckIn, fixedFlashSelectionMode, onClearStayTooltips])

  const dispatch = useOfferPageDispatch()
  const onInvalidBundleDateClick = useCallback(
    () => {
      if (isBundleAndSaveV2Enabled()) {
        showSnackbar(intl.formatMessage({
          id: 'dmwnUxVqI9FJFFG__kIZ',
          defaultMessage: 'Check-in date overlaps with your other stay. Please select a new date.',
          description: 'Snackbar warning when selected check-in date overlaps with another hotel stay',
        }), 'warning')
      } else {
        showSnackbar(
          intl.formatMessage({
            id: '0C_gjBUZcklsw_kX_Wdj',
            defaultMessage: 'The dates for Hotel 2 must come after Hotel 1. Try swapping the order of the hotels.',
            description: 'Snackbar warning when Hotel 2 dates conflict with Hotel 1 dates in bundle',
          }),
          'warning',
          {
            action: {
              label: intl.formatMessage({
                id: '9j4PH_THjnq46iTfsXuH',
                defaultMessage: 'Swap order',
                description: 'Button label to swap the order of hotels in a bundle',
              }),
              onAction: () => {
                dispatch({ type: OfferPageActions.openSwapModal })
              },
            },
          },
        )
      }
    },
    [dispatch, intl],
  )

  const villasDiscounts = isVillaOffer(offer) ? getBestVillasDiscounts(offer.discounts) : undefined
  const hideChildPrices = offer.type === 'tactical_ao_hotel' && !!offer.property?.hideChildPrices && rooms?.some(room => room.children !== undefined && room.children > 0)
  const villaSoldOutText = isVillaOffer(offer)
    ? intl.formatMessage({
        id: 'a43RpN_N7jI2dAqbs5Uo',
        defaultMessage: 'Check-in unavailable',
        description: 'Text shown when check-in is unavailable for a villa offer on a calendar day',
      })
    : undefined

  return (
    <div className={className}>
      <BaseGrid>
        {orderCalendarWeekdays([dayNames[6], ...dayNames.slice(0, 6)], weekStart).map((day) => (
          <Caption variant="large" as="abbr" title={day.full} key={day.full}>{day.short}</Caption>
        ))}
      </BaseGrid>
      <Grid>
        {emptyCells.map((cell, index) =>
          <EmptyCell style={{ gridColumnStart: cell.column }} key={index}>
            <Heading colour="light-foreground-alt" variant="heading6">{cell.day}</Heading>
          </EmptyCell>,
        )}
        {days.map((day, index) => {
          const {
            checkoutCalendarDay,
            checkInCalendarDay,
            isFixedLengthCombinationUnavailable,
            isMinimumStayCheckoutDisabled,
            isMaximumStayDisabled,
          } = getFixedFlashCalendarDayState({
            day,
            selectedCheckIn,
            selectedCheckOut,
            checkoutCalendarDays: fixedFlashCheckoutCalendarDays,
            checkInCalendarDays: fixedFlashCheckInCalendarDays,
            isFixedFlashLengthOfStayDateSelection,
            minimumCheckoutDuration,
            maximumDuration: fixedFlashMaximumDuration,
          })
          const resolvedCheckoutCalendarDay = selectedCheckOut && day.checkIn !== selectedCheckOut ? undefined : checkoutCalendarDay
          const canHoverCheckOut = !!(
            fixedFlashSelectionMode === 'select-check-out' &&
            selectedCheckIn &&
            moment(day.checkIn).isAfter(selectedCheckIn, 'day') &&
            checkoutCalendarDay &&
            !isMinimumStayCheckoutDisabled &&
            !isMaximumStayDisabled &&
            !isFixedLengthCombinationUnavailable
          )
          const isHoveredCheckOut = canHoverCheckOut && hoveredCheckOut === day.checkIn

          return <OfferCalendarDayCell
            key={day.checkIn}
            duration={duration}
            bundleOfferId={bundleOfferId}
            checkIn={day.checkIn}
            style={(index === 0 && emptyCells.length === 0) ? startCellStyle : undefined}
            day={day}
            checkoutCalendarDay={resolvedCheckoutCalendarDay}
            checkInCalendarDay={checkInCalendarDay}
            hasOtherAvailability={otherAvailability[day.checkIn] && !bundleOfferId}
            isCheckIn={day.checkIn === selectedCheckIn}
            isCheckOut={day.checkIn === selectedCheckOut}
            canHoverCheckOut={canHoverCheckOut}
            hasHoveredCheckOut={!!hoveredCheckOut}
            isHoveredCheckOut={isHoveredCheckOut}
            onHoveredCheckOutChange={onHoveredCheckOutChange}
            selectedCheckIn={selectedCheckIn}
            selectedCheckOut={selectedCheckOut}
            isFixedFlashLengthOfStayEnabled={isFixedFlashLengthOfStayEnabled}
            minimumDuration={minimumDuration}
            isFixedLengthCombinationUnavailable={isFixedLengthCombinationUnavailable}
            isMinimumStayCheckoutDisabled={isMinimumStayCheckoutDisabled}
            isMaximumStayDisabled={isMaximumStayDisabled}
            checkInMinimumStayTooltipOpen={isFixedFlashLengthOfStayDateSelection && day.checkIn === selectedCheckIn && !minimumStayTooltipDate && !maximumStayTooltipDate}
            checkInMinimumStayTooltipDescription={minimumStayTooltipDescription}
            minimumStayTooltipOpen={minimumStayTooltipDate === day.checkIn}
            minimumStayTooltipDescription={minimumStayTooltipDescription}
            onMinimumStayDisabledClick={onMinimumStayDisabledClick}
            maximumStayTooltipOpen={maximumStayTooltipDate === day.checkIn}
            maximumStayTooltipDescription={maximumStayTooltipDescription}
            onMaximumStayDisabledClick={onMaximumStayDisabledClick}
            isBetweenDay={!isMaximumStayDisabled && !!(betweenRange && (index + 1) >= betweenRange.start && index < (betweenRange.end - 1))}
            isRoomSelected={isRoomSelected}
            flights={isAnyFlightsActive}
            onClick={onDayClick}
            flightsOnly={flightsOnly}
            currentOfferId={currentOfferId}
            enableCalendarRecommendations={enableCalendarRecommendations}
            isComplementaryBundleCell={isComplementaryBundleCell(day)}
            isComplementaryCheckIn={isComplementaryCheckInCell(day)}
            isComplementaryCheckOut={isComplementaryCheckOutCell(day)}
            isIllegalBundleSelection={isIllegalBundleSelection(day)}
            complementaryPropertyName={complementaryBundleState?.offer?.property?.name}
            hasPromo={checkHasPromo(day)}
            isTacticalPromotion={tacticalDays.includes(day.checkIn) && day.hotelAvailable}
            cheapestDay={cheapestDays[day.uniqueKey] ?? null}
            onInvalidBundleDateClick={onInvalidBundleDateClick}
            showUrgencyLabels={!isVillaOffer(offer)}
            soldOutText={villaSoldOutText}
            displayMemberPrice={displayMemberPrice}
            userPoints={userPoints}
            loyaltyProductType={loyaltyProductType}
            villasDiscounts={villasDiscounts}
            hideChildPrices={hideChildPrices}
            markCheapestPrice={markCheapestPrice}
            tier={tier}
            canLoyaltyUpgrade={canLoyaltyUpgrade}
          />
        })}
      </Grid>
    </div>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    offer: state.offer,
    bundleOfferId: state.bundleOffer?.id,
    duration: state.duration,
    selectedCheckIn: state.checkInDate,
    rooms: state.rooms,
    currentOfferId: state.paginator?.currentOfferId,
    complementaryBundleState: getComplementaryBundleState(state),
    enableCalendarRecommendations: !state.bundleOffer ? shouldEnableCalendarRecommendations(state.offer) : false,
    isAnyFlightsActive: state.flights,
    cheapestDays: getCalendarCheapestDays(currentState, appState),
    displayMemberPrice: currentState.displayMemberPrice,
    userPoints: appState.loyalty.account.data?.points ?? 0,
    markCheapestPrice: markCheapestPrice(state),
    open: !!state.activeFilter,
  }
}, OfferCalendarDaysMonth, preventRenderWithClosedFilter)
